<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    id="offcanvaInfo"
    ref="offcanvaInfo"
    data-bs-backdrop="static"
  >
    <!-- Header -->
    <div class="offcanvas-header py-3 px-4" style="background: linear-gradient(135deg, #4a6cf7 0%, #6366f1 100%);">
      <div class="d-flex align-items-center">
        <div class="rounded-circle d-flex align-items-center justify-content-center me-3"
          style="width: 36px; height: 36px; background: rgba(255,255,255,0.2);">
          <i class="fa-solid fa-file-invoice text-white" />
        </div>
        <div>
          <h6 class="mb-0 text-white fw-semibold">
            {{ $t('Goods Receipt Detail') }}
          </h6>
        </div>
      </div>
      <button
        type="button"
        class="btn-close btn-close-white shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <!-- Loading -->
    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>

    <!-- Body -->
    <div class="offcanvas-body p-0" style="background-color: #f8f9fc;" v-else>

      <!-- Document Header -->
      <div class="bg-white border-bottom px-4 py-3">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <h5 class="mb-1 fw-bold" style="color: #2d3748;">
              {{ data.document_no }}
            </h5>
            <div class="d-flex align-items-center gap-2">
              <span
                class="badge rounded-pill"
                :class="data.status === 'received' ? 'bg-success' : 'bg-danger'"
                style="font-size: 11px; text-transform: capitalize;"
              >
                <i class="fa-solid me-1" :class="data.status === 'received' ? 'fa-check-circle' : 'fa-times-circle'" style="font-size: 9px;" />
                {{ $t(data.status) }}
              </span>
              <span
                v-if="data.tax_policy && data.tax_policy !== 'no_tax'"
                class="badge rounded-pill"
                :class="data.tax_policy === 'included' ? 'bg-info bg-opacity-10 text-info' : 'bg-warning bg-opacity-10 text-warning'"
                style="font-size: 11px;"
              >
                <i class="fa-solid fa-percent me-1" style="font-size: 8px;" />
                {{ data.tax_policy === 'included' ? $t('VAT Included') : $t('VAT Excluded') }}
              </span>
              <span
                v-if="!data.tax_policy || data.tax_policy === 'no_tax'"
                class="badge rounded-pill bg-secondary bg-opacity-10 text-secondary"
                style="font-size: 11px;"
              >
                {{ $t('No Tax') }}
              </span>
            </div>
          </div>
          <div class="text-end">
            <small class="text-muted d-block" style="font-size: 11px;">
              {{ $t('Last Update') }}
            </small>
            <small class="text-muted" style="font-size: 12px;">
              {{ formatDateTime(data.updated_at) }}
            </small>
          </div>
        </div>
      </div>

      <!-- Section: Information -->
      <div class="card border-0 rounded-0 shadow-sm mx-0 mb-0 mt-2">
        <div class="card-header bg-white border-bottom py-2 px-4">
          <span class="fw-semibold" style="font-size: 13px;">
            <i class="fa-solid fa-circle-info me-2 text-primary" />
            {{ $t('Goods Receipt Information') }}
          </span>
        </div>
        <div class="card-body px-4 py-3">
          <div class="row g-3" style="font-size: 13px;">
            <!-- Received Date -->
            <div class="col-6 col-md-4">
              <div class="d-flex align-items-start">
                <div class="rounded-2 d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                  style="width: 32px; height: 32px; background: #eef2ff;">
                  <i class="fa-solid fa-calendar-days text-primary" style="font-size: 13px;" />
                </div>
                <div>
                  <small class="text-muted d-block" style="font-size: 11px;">{{ $t('Received Date') }}</small>
                  <span class="fw-medium">{{ formatDate(data.transaction_date) }}</span>
                </div>
              </div>
            </div>
            <!-- Invoice -->
            <div class="col-6 col-md-4">
              <div class="d-flex align-items-start">
                <div class="rounded-2 d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                  style="width: 32px; height: 32px; background: #eef2ff;">
                  <i class="fa-solid fa-file-invoice text-primary" style="font-size: 13px;" />
                </div>
                <div>
                  <small class="text-muted d-block" style="font-size: 11px;">{{ $t('Invoice') }}</small>
                  <span class="fw-medium">{{ data.ref_no ? data.ref_no : '-' }}</span>
                </div>
              </div>
            </div>
            <!-- Supplier -->
            <div class="col-6 col-md-4">
              <div class="d-flex align-items-start">
                <div class="rounded-2 d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                  style="width: 32px; height: 32px; background: #eef2ff;">
                  <i class="fa-solid fa-store text-primary" style="font-size: 13px;" />
                </div>
                <div>
                  <small class="text-muted d-block" style="font-size: 11px;">{{ $t('Supplier') }}</small>
                  <span class="fw-medium">{{ data.supplier }}</span>
                </div>
              </div>
            </div>
            <!-- Branch -->
            <div class="col-6 col-md-4">
              <div class="d-flex align-items-start">
                <div class="rounded-2 d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                  style="width: 32px; height: 32px; background: #eef2ff;">
                  <i class="fa-solid fa-building text-primary" style="font-size: 13px;" />
                </div>
                <div>
                  <small class="text-muted d-block" style="font-size: 11px;">{{ $t('Branch') }}</small>
                  <span class="fw-medium">{{ data.branch }}</span>
                </div>
              </div>
            </div>
            <!-- Created By -->
            <div class="col-6 col-md-4">
              <div class="d-flex align-items-start">
                <div class="rounded-2 d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                  style="width: 32px; height: 32px; background: #eef2ff;">
                  <i class="fa-solid fa-user-pen text-primary" style="font-size: 13px;" />
                </div>
                <div>
                  <small class="text-muted d-block" style="font-size: 11px;">{{ $t('Create By') }}</small>
                  <span class="fw-medium">{{ data.created_by }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section: Items -->
      <div class="card border-0 rounded-0 shadow-sm mx-0 mb-0 mt-2">
        <div class="card-header bg-white border-bottom py-2 px-4">
          <span class="fw-semibold" style="font-size: 13px;">
            <i class="fa-solid fa-basket-shopping me-2 text-primary" />
            {{ $t('Items') }}
            <span v-if="datatableItems.items.length > 0" class="badge bg-primary rounded-pill ms-1">
              {{ datatableItems.items.length }}
            </span>
          </span>
        </div>
        <div class="card-body p-0">
          <vue3-easy-data-table
            ref="datatableItems"
            :headers="datatableItems.headers($t)"
            :items="datatableItems.items"
            :loading="datatableItems.loading"
            :theme-color="'#4a6cf7'"
            :hide-footer="true"
            :hide-rows-per-page="true"
            :empty-message="$t('No Available Data')"
            :table-min-height="null"
            :body-item-class-name="bodyItemClassNameFunction"
            table-class-name='customize-table-hide-footer-in-offcanvas'
          >
            <template #header-row_number="header">
              <span class="w-100 text-center">{{ header.text }}</span>
            </template>
            <template #item-row_number="item">
              {{ item.row_number }}
            </template>
            <template #header-ordered_quantity="header">
              <span class="w-100 text-end">{{ header.text }}</span>
            </template>
            <template #header-price="header">
              <span class="w-100 text-end">{{ header.text }}</span>
            </template>
            <template #header-discount="header">
              <span class="w-100 text-end">{{ header.text }}</span>
            </template>
            <template #header-total_before_vat="header">
              <span class="w-100 text-end">{{ header.text }}</span>
            </template>
            <template #header-total_vat="header">
              <span class="w-100 text-end">{{ header.text }}</span>
            </template>
            <template #header-total="header">
              <span class="w-100 text-end">{{ header.text }}</span>
            </template>
            <template #item-name="item">
              <span class="fw-medium">{{ item.name }}</span>
              <span
                v-if="data.tax_policy && data.tax_policy !== 'no_tax'"
                class="badge ms-1"
                :class="item.tax_status === 'vatable'? 'bg-info bg-opacity-10 text-info': 'bg-secondary bg-opacity-10 text-secondary'"
                style="font-size: 9px; font-weight: 500;"
              >
                {{ item.tax_status === 'vatable'? $t('VATable'): $t('Non-VAT') }}
              </span>
            </template>
            <template #item-unit="item">
              {{ $t(item.unit) }}
            </template>
            <template #item-price="item">
              {{ formatDigit(item.price) }}
            </template>
            <template #item-discount="item">
              {{ formatDigit(item.discount) }}
            </template>
            <template #item-total_before_vat="item">
              {{ formatDigit(item.total_before_vat) }}
            </template>
            <template #item-total_vat="item">
              {{ formatDigit(item.total_vat) }}
            </template>
            <template #item-total="item">
              <span class="fw-semibold">{{ formatDigit(item.total) }}</span>
            </template>
            <template #loading>
              <i class="fa-solid fa-spinner fa-spin fa-2xl" />
            </template>
          </vue3-easy-data-table>
        </div>
      </div>

      <!-- Section: Summary -->
      <div class="card border-0 rounded-0 shadow-sm mx-0 mb-0 mt-2">
        <div class="card-header bg-white border-bottom py-2 px-4">
          <span class="fw-semibold" style="font-size: 13px;">
            <i class="fa-solid fa-calculator me-2 text-primary" />
            {{ $t('Summary') }}
          </span>
        </div>
        <div class="card-body px-4 py-3">
          <div class="row g-3">
            <!-- Note -->
            <div class="col-md-6">
              <div class="d-flex align-items-start">
                <i class="fa-solid fa-note-sticky text-muted me-2 mt-1" style="font-size: 13px;" />
                <div>
                  <small class="text-muted d-block mb-1" style="font-size: 11px;">{{ $t('note') }}</small>
                  <span style="font-size: 13px; white-space: pre-wrap;">{{ data.note ? data.note : '-' }}</span>
                </div>
              </div>
            </div>
            <!-- Totals -->
            <div class="col-md-6">
              <div class="rounded-3 p-3" style="background: #f8f9fc; border: 1px solid #e9ecef;">
                <table class="w-100" style="font-size: 13px;">
                  <tbody>
                    <!-- Sub Total -->
                    <tr>
                      <td class="pb-2 text-muted">{{ $t('Sub Total') }}</td>
                      <td class="text-end pb-2 fw-medium">{{ formatDigit(data.sub_total) }}</td>
                    </tr>

                    <!-- Shipping -->
                    <tr>
                      <td class="pb-2 text-muted">{{ $t('Shipping') }}</td>
                      <td class="text-end pb-2 fw-medium">{{ formatDigit(data.shipping_price) }}</td>
                    </tr>

                    <!-- Discount -->
                    <tr>
                      <td class="pb-2 text-muted">{{ $t('Discount') }}</td>
                      <td class="text-end pb-2 fw-medium">{{ formatDigit(data.discount) }}</td>
                    </tr>

                    <!-- Total Before VAT & VAT -->
                    <tr v-if="data.tax_policy && data.tax_policy !== 'no_tax'">
                      <td colspan="2"><hr class="my-1" style="border-color: #dee2e6;" /></td>
                    </tr>
                    <tr v-if="data.tax_policy && data.tax_policy !== 'no_tax'">
                      <td class="pb-1 pt-1" style="font-size: 12px;">{{ $t('Total Before VAT') }}</td>
                      <td class="text-end pb-1 pt-1" style="font-size: 12px;">{{ formatDigit(data.total_before_vat) }}</td>
                    </tr>
                    <tr v-if="data.tax_policy && data.tax_policy !== 'no_tax'">
                      <td class="pb-2" style="font-size: 12px;">{{ $t('Total VAT') }}</td>
                      <td class="text-end pb-2" style="font-size: 12px;">{{ formatDigit(data.total_vat) }}</td>
                    </tr>

                    <!-- Grand Total -->
                    <tr>
                      <td colspan="2"><hr class="my-1" style="border-color: #adb5bd;" /></td>
                    </tr>
                    <tr>
                      <td class="pt-2">
                        <b style="font-size: 15px;">{{ $t('Grand Total') }}</b>
                      </td>
                      <td class="text-end pt-2">
                        <b style="font-size: 15px; color: #4a6cf7;">{{ formatDigit(data.total) }}</b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="border-top d-flex justify-content-center align-items-center py-3 px-4"
      style="background: #f8f9fc;"
      v-if="!loading"
    >
      <div
        class="btn-group dropdown"
        v-if="data.status === 'received'"
      >
        <button
          type="button"
          class="btn btn-sm btn-primary dropdown-toggle"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
        >
          <i class="fa-solid fa-ellipsis me-1" :class="isHovered? 'fa-fade': ''" />
          {{ $t('More Action') }}
        </button>
        <ul class="dropdown-menu">
          <li>
            <a
              class="dropdown-item"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              @click="() => {
                $parent.offcanvasFroms.open = true;
                $parent.offcanvasFroms.id = id;
                $parent.offcanvasFroms.type = 'EDIT';
              }"
            >
              <i class="fa-solid fa-square-pen me-2" />
              {{ $t('Edit') }}
            </a>
          </li>
          <li>
            <a
              class="dropdown-item text-danger"
              @click="handleCancel()"
            >
              <i class="fa-solid fa-ban me-2" />
              {{ $t('Cancel') }}
            </a>
          </li>
        </ul>
      </div>
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="btn btn-sm btn-outline-secondary ms-2"
      >
        {{ $t('Close') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import { GoodsReceipts } from '@/services/endpoint/goods-receipts.service';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import Ladda from '@/plugins/Ladda.vue';
import moment from "moment";
import numeral from 'numeral';
import { distance } from '@/store/date-fns'
import Popper from "vue3-popper";
import FieldItems from "./assets/template/datatable/FieldItems";

export default {
  props: {
    id: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      datatableItems: {
        loading: false,
        headers: FieldItems,
        items: [],
        total: 0,
        params: {
          limit: 10,
          page: 1,
          search: ''
        }
      },
      isHovered: false,
      loadingCard: true,
      loading: true,
      data: {
        name: '',
        code: '',
        quantity: 0,
        cost: 0,
        unit_id: '1',
      },
      tabCards: [
        {
          id: 1,
          name: 'Activity Logs',
          disabled: false
        },
        {
          id: 2,
          name: 'Unusable',
          disabled: false
        }
      ],
      selectTabCard: 1
    }
  },
  components: {
    Vue3EasyDataTable,
    PlaceholderContent,
    Ladda,
    Popper
  },
  methods: {
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top border-0';
      const columnStart = ['row_number', 'name', 'lot_number', 'unit'];
      const columnCenter = [];
      const columnEnd = ['ordered_quantity', 'price', 'discount', 'total_before_vat', 'total_vat', 'total'];
      if (columnStart.includes(column)) {
        className += ' text-start';
      }
      if (columnCenter.includes(column)) {
        className += ' text-center';
      }
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async getInfoData() {
      this.loading = true;
      await GoodsReceipts.getInfo(this.id).then(res => {
        this.data = res.data.data;
        this.datatableItems.items = res.data.data.items.map((item, i) => ({ ...item, row_number: i + 1 }));
        this.loading = false;
      }).catch(err => {
			  throw err;
      });
    },
    formatDate(value) {
      return moment(value).format('DD/MM/YYYY');
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    dateDistance(value) {
      return distance(value);
    },
    async handleCancel() {
      try {
        let self = this;
        let title = this.$t("Confirm Cancellation") + '?';
        let html = this.$t('Are you sure you want to cancel Goods Receipt') + `<br><b>${this.data.document_no}</b><br><small class="text-danger" style="font-size: 0.9em;">⚠️ ` +this.$t("Warning: This will");
          html+=' <b>'+this.$t("reverse stock")+'</b> '+this.$t("and")+' <b>'+this.$t("recalculate costs")+'</b> '+this.$t('immediately.')+'<br>'+this.$t('This action cannot be undone.');
          html+='</small>';
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Yes, Cancel Now");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("No, Keep it"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-danger me-2",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          reverseButtons: true,
          focusCancel: true,
          preConfirm: async function () {
            return GoodsReceipts.cancel(self.id)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Cancel Goods Receipt Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  },
  async mounted() {
    $(this.$refs.offcanvaInfo).offcanvas('show');

    $(this.$refs.offcanvaInfo).on('shown.bs.offcanvas',async () => {
      try {
        await this.getInfoData();
      } catch (err) {
      }
    });

    $(this.$refs.offcanvaInfo).on('hidden.bs.offcanvas', () => {
      this.$parent.formInfo.open = false;
    });
  },
  computed: {

  },
  watch: {
  }
}
</script>
